
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Workflow, CheckCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { InfoIcon } from "lucide-react";
import { toast } from "sonner";
import { TimePicker } from "@/components/ui/TimePicker";

interface WorkflowSectionProps {
  schedule: string;
  time: string;
  days: string[];
  onScheduleChange: (value: string) => void;
  onTimeChange: (value: string) => void;
  onDaySelection: (day: string) => void;
}

export const WorkflowSection = ({
  schedule,
  time,
  days,
  onScheduleChange,
  onTimeChange,
  onDaySelection,
}: WorkflowSectionProps) => {
  const handleTimeChange = (newTime: string) => {
    onTimeChange(newTime);
    if (newTime) {
      const timeObj = new Date(`2000-01-01T${newTime}`);
      const formattedTime = timeObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      toast.success(`Workflow time set to ${formattedTime}`);
    }
  };

  const handleScheduleChange = (value: string) => {
    onScheduleChange(value);
    if (value === 'custom') {
      toast.info('Please select days for your custom schedule');
    } else {
      toast.success(`Workflow frequency set to ${value}`);
    }
  };

  const handleDayToggle = (day: string) => {
    onDaySelection(day);
    const isSelected = days.includes(day);
    if (isSelected) {
      toast.info(`${day} removed from schedule`);
    } else {
      toast.success(`${day} added to schedule`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <Workflow className="h-5 w-5 text-primary" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Label>Full Workflow Automation</Label>
            <HoverCard>
              <HoverCardTrigger asChild>
                <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <p className="text-sm">
                  Schedule your complete workflow execution including data collection, 
                  cleaning, analysis, and reporting at your preferred frequency.
                </p>
                <div className="mt-2 text-xs text-muted-foreground">
                  <p>• Daily: Runs every day at the specified time</p>
                  <p>• Weekly: Runs every Monday at the specified time</p>
                  <p>• Custom: Runs on selected days at the specified time</p>
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
          <p className="text-sm text-muted-foreground">
            Schedule complete workflow execution with data processing
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm">Frequency</Label>
          <Select value={schedule || ""} onValueChange={handleScheduleChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="custom">Custom Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label className="text-sm">Time</Label>
          <TimePicker value={time || "09:00"} onChange={handleTimeChange} />
          {time && (
            <p className="text-xs text-green-600">
              ✓ Time set
            </p>
          )}
        </div>
      </div>
      
      {schedule === 'custom' && (
        <div className="space-y-2">
          <Label className="text-sm">Select days for custom schedule:</Label>
          <div className="flex flex-wrap gap-2">
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
              <Button
                key={day}
                variant={days.includes(day) ? "default" : "outline"}
                size="sm"
                onClick={() => handleDayToggle(day)}
                className="flex items-center gap-1"
                title={`${days.includes(day) ? 'Remove' : 'Add'} ${day}`}
              >
                {days.includes(day) && <CheckCircle className="h-3 w-3" />}
                {day.slice(0, 3)}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {days.length > 0 
              ? `Tasks will run on ${days.join(', ')} at ${time || 'the specified time'}`
              : 'Select at least one day for your custom schedule'
            }
          </p>
        </div>
      )}
      
      {schedule && time && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <CheckCircle className="inline h-4 w-4 mr-1" />
            Workflow scheduled: {schedule} at {new Date(`2000-01-01T${time}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {schedule === 'custom' && days.length > 0 && ` on ${days.join(', ')}`}
          </p>
        </div>
      )}
    </div>
  );
};
